import { useState, useEffect, useMemo, useCallback, useRef } from "react";

/**
 * useLaundryItems
 * Shared hook for managing the dynamic item rows in both Add and Edit laundry forms.
 *
 * Handles:
 *  - Local items state
 *  - Pre-fill from existing data (edit mode) — runs only once
 *  - Total cost calculation per row
 *  - Product/task validation
 *  - Syncing items → parent values (called directly, no useEffect loop)
 *  - Row add / delete
 *  - Grand total
 *
 * @param {Object} params
 * @param {Array}   params.TaskOptions      - All task options from API
 * @param {Array}   params.ProductOptions   - All product options from API
 * @param {any}     params.existingItems    - values.items from parent (edit pre-fill)
 * @param {Function} params.setValues       - Parent setValues
 * @param {Function} params.setErrors       - Parent setErrors
 * @param {Function} params.validateItems   - Item validation function
 */
const useLaundryItems = ({
    TaskOptions,
    ProductOptions,
    existingItems,
    setValues,
    setErrors,
    validateItems,
}) => {
    const [items, setItems] = useState([
        { product_id: "", task_id: [], quantity: "", totle: 0 },
    ]);

    // Tracks whether the pre-fill has already run (edit mode guard)
    const [prefilled, setPrefilled] = useState(false);

    // Stores the original saved quantity per product_id.
    // Used for the quantity placeholder on edit so it shows the correct max
    // even before row.quantity is populated from the pre-fill.
    const originalQtyRef = useRef({});

    // ── Calculate total cost for a single row ─────────────────────────────
    const calculateTotal = useCallback(
        (row) => {
            const qty = Number(row.quantity || 0);
            if (!qty || !row.product_id || !Array.isArray(row.task_id) || !row.task_id.length)
                return 0;
            const taskTotal = row.task_id.reduce((sum, taskId) => {
                const task = TaskOptions.find(
                    (t) =>
                        Number(t.product_id) === Number(row.product_id) &&
                        Number(t.task_id) === Number(taskId)
                );
                return sum + Number(task?.price || 0);
            }, 0);
            return taskTotal * qty;
        },
        [TaskOptions]
    );

    // ── Pre-fill items once when existing data loads (edit mode) ──────────
    useEffect(() => {
        if (prefilled) return;
        if (!Array.isArray(existingItems) || existingItems.length === 0) return;

        const mapped = existingItems.map((it) => ({
            product_id: Number(it.product_id),
            task_id: Array.isArray(it.task_id) ? it.task_id.map(Number) : [],
            quantity: it.quantity,
            totle: calculateTotal({
                product_id: Number(it.product_id),
                task_id: Array.isArray(it.task_id) ? it.task_id.map(Number) : [],
                quantity: it.quantity,
            }),
        }));

        // Capture original quantities for placeholder calculation
        mapped.forEach((it) => {
            originalQtyRef.current[Number(it.product_id)] = Number(it.quantity) || 0;
        });

        setItems(mapped);
        setPrefilled(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [existingItems]);

    // ── Check if a task belongs to a given product ────────────────────────
    const isValidTaskForProduct = useCallback(
        (productId, taskId) =>
            TaskOptions.some(
                (t) =>
                    Number(t.product_id) === Number(productId) &&
                    Number(t.task_id) === Number(taskId)
            ),
        [TaskOptions]
    );

    // ── Product options excluding already-selected products in other rows ─
    const getAvailableProductOptions = useCallback(
        (currentRowIndex) => {
            const selectedIds = items
                .map((row, idx) => (idx !== currentRowIndex ? row.product_id : null))
                .filter(Boolean);
            return ProductOptions.filter(
                (p) =>
                    !selectedIds.includes(p.product_id) ||
                    p.product_id === items[currentRowIndex]?.product_id
            );
        },
        [items, ProductOptions]
    );

    // ── Internal helper: update items + sync to parent ────────────────────
    const commitItems = useCallback(
        (updated) => {
            const itemErrors = validateItems(updated);
            setErrors((prev) => ({ ...prev, items: itemErrors }));
            setValues((prev) => ({ ...prev, items: updated }));
        },
        [validateItems, setErrors, setValues]
    );

    // ── Handle a field change in a row ────────────────────────────────────
    const handleItemChange = useCallback(
        (index, name, value) => {
            setItems((prevItems) => {
                const updated = prevItems.map((row, i) => {
                    if (i !== index) return row;
                    let updatedRow = { ...row, [name]: value };

                    // Product changed → clear tasks that don't belong to new product
                    if (name === "product_id") {
                        updatedRow.task_id = (row.task_id || [])
                            .map(Number)
                            .filter((tid) => isValidTaskForProduct(value, tid));
                    }
                    // Task changed → normalise to numbers and validate against product
                    if (name === "task_id") {
                        updatedRow.task_id = (value || [])
                            .map((v) => Number(v?.value ?? v))
                            .filter((tid) => isValidTaskForProduct(row.product_id, tid));
                    }
                    return { ...updatedRow, totle: calculateTotal(updatedRow) };
                });
                commitItems(updated);
                return updated;
            });
        },
        [calculateTotal, isValidTaskForProduct, commitItems]
    );

    const addRow = useCallback(() => {
        setItems((prev) => {
            const updated = [...prev, { product_id: "", task_id: [], quantity: "", totle: 0 }];
            commitItems(updated);
            return updated;
        });
    }, [commitItems]);

    const deleteRow = useCallback(
        (index) => {
            setItems((prev) => {
                if (prev.length <= 1) return prev;
                const updated = prev.filter((_, i) => i !== index);
                commitItems(updated);
                return updated;
            });
        },
        [commitItems]
    );

    const grandTotal = useMemo(
        () => items.reduce((sum, it) => sum + Number(it.totle || 0), 0),
        [items]
    );

    return {
        items,
        grandTotal,
        originalQtyRef,
        handleItemChange,
        addRow,
        deleteRow,
        getAvailableProductOptions,
        isValidTaskForProduct,
        calculateTotal,
    };
};

export default useLaundryItems;
